import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Media from '@/components/base/media'
import Motion from '@/components/base/motion'
import ParallaxImage from '@/components/base/parallax-image'
import ScrollCta from '@/components/base/scroll-cta'
import Typography from '@/components/base/typography'
import {
  imageHeroRevealAnimation,
  imageHeroTwoColAnimation
} from '@/components/sections/animations/image-hero'

import { cn } from '@/lib/utils'
import { WPMedia } from '@/types'

type ImageHeroProps = {
  type: 'editorial' | '2_col'
  title?: string
  text?: string
  media: WPMedia
  titleComponent?: 'h1' | 'h2'
}

const ImageHero = ({
  type,
  title,
  text,
  media,
  titleComponent = 'h1',
  ...props
}: ImageHeroProps) => {
  const renderEditorial = () => (
    <Motion animation={imageHeroRevealAnimation}>
      {(motionRef) => (
        <div
          ref={motionRef}
          className="relative h-[calc(var(--lvh)*100)]"
          data-type={type}
          {...props}
        >
          {(media?.poster?.url || media?.video?.url) && (
            <ParallaxImage
              className={cn('absolute inset-0 z-0', {
                'z-20': !title && !text
              })}
              offset={-40}
            >
              {title && text && (
                <div className="pointer-events-none absolute inset-0 z-10 h-full w-full bg-[black]/20" />
              )}
              <Media
                media={media}
                unlockLoaderOnReady
                priority
                data-motion-media
                data-lightbox={!title && !text}
                className={cn('absolute left-0 top-0 h-full w-full')}
              />
            </ParallaxImage>
          )}

          <Container className="dark relative z-10 h-full">
            <Grid className="relative h-full">
              <div className="col-span-2 md:col-span-5 lg:col-span-5">
                <div
                  className={cn(
                    'flex h-full flex-col justify-end gap-spacing-xl pb-[140px] md:pb-spacing-2xl'
                  )}
                >
                  {title ? (
                    <Typography
                      variant="h2"
                      component={titleComponent}
                      data-motion-title
                    >
                      {title}
                    </Typography>
                  ) : null}
                  {text ? (
                    <Typography
                      variant="regular"
                      component="p"
                      className="text-text-primary-light dark:text-text-primary-dark"
                      data-motion-text
                    >
                      {text}
                    </Typography>
                  ) : null}

                  {title && text && (
                    <ScrollCta
                      className="absolute bottom-[20px] left-0 right-auto md:bottom-spacing-2xl md:left-auto md:right-0"
                      data-motion-cta
                    />
                  )}
                </div>
              </div>
            </Grid>
          </Container>
        </div>
      )}
    </Motion>
  )

  const renderTwoCol = () => (
    <Motion animation={imageHeroTwoColAnimation}>
      {(motionRef) => (
        <div
          ref={motionRef}
          className="relative mt-[calc(var(--header-height)*-1-43px)] h-auto md:h-[calc(var(--lvh)*200)]"
          data-type={type}
          {...props}
        >
          <Container
            className="sticky top-0 h-auto max-w-full overflow-hidden pt-[calc(var(--header-height)+43px)] md:h-[calc(var(--lvh)*100)]"
            data-motion-container
          >
            <Grid className="md:h-[calc(var(--lvh)*100))] relative flex h-full flex-col justify-between md:grid">
              <div className="relative z-10 col-span-2 md:col-span-3 lg:col-span-5">
                <div
                  className={cn(
                    'flex flex-[1_0_auto] flex-col justify-start gap-spacing-xl pb-[100px] pt-[40px] md:h-full md:pb-spacing-2xl lg:pt-spacing-2xl'
                  )}
                >
                  {title ? (
                    <Typography
                      variant="h2"
                      component={titleComponent}
                      data-motion-title
                    >
                      {title}
                    </Typography>
                  ) : null}
                  {text ? (
                    <Typography
                      variant="regular"
                      component="p"
                      className="text-text-primary-light dark:text-text-primary-dark"
                      data-motion-text
                    >
                      {text}
                    </Typography>
                  ) : null}

                  <ScrollCta
                    className="absolute bottom-[20px] left-0 z-20 md:bottom-spacing-2xl"
                    data-motion-cta
                  />
                </div>
              </div>
              <div
                className="relative z-0 col-span-2 mr-0 flex-[0_0_50%] md:col-span-3 md:-mr-[30px] md:!aspect-auto md:h-full lg:col-span-7 lg:col-start-6 lg:-mr-[60px]"
                style={{
                  aspectRatio:
                    media?.poster?.width && media?.poster?.height
                      ? `${media.poster.width} / ${media.poster.height}`
                      : '4/3'
                }}
              >
                {(media?.poster?.url || media?.video?.url) && (
                  <div
                    data-motion-media
                    className="relative inset-0 z-0 md:absolute md:aspect-auto md:h-full"
                    style={{
                      aspectRatio:
                        media?.poster?.width && media?.poster?.height
                          ? `${media.poster.width} / ${media.poster.height}`
                          : '4/3'
                    }}
                  >
                    <Media
                      media={media}
                      unlockLoaderOnReady
                      priority
                      className={cn('absolute left-0 top-0 h-full w-full')}
                    />
                  </div>
                )}
              </div>
            </Grid>
          </Container>
        </div>
      )}
    </Motion>
  )

  return type === 'editorial' ? renderEditorial() : renderTwoCol()
}

export default ImageHero
